import { supabase } from '../supabaseClient';
import { WeeklyLearningGuideNote, WeeklyLearningGuideNoteUpsert } from '../types/domain';
import { canWriteSchoolScopedDataCache, notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import {
  loadWeeklyLearningGuideNotesCache,
  saveWeeklyLearningGuideNotesCache,
} from '../utils/weeklyLearningGuideNotesCache';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

const WEEKLY_LEARNING_GUIDE_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'academic_year',
  'term_number',
  'monday_iso',
  'note_text',
  'created_at',
  'updated_at',
].join(', ');

const CACHE_VERSION = 'v1';

function cacheKey(workspaceId: string, academicYear: number, termNumber: 1 | 2, gradeId: string): string {
  return `cp.weeklyLearningGuideNotes.${CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function localFallbackKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): string {
  return `cp.weeklyLearningGuideNotes.local.${CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${gradeId}`;
}

function normalizeRows(rows: WeeklyLearningGuideNote[]): WeeklyLearningGuideNote[] {
  return [...rows].sort((a, b) => a.monday_iso.localeCompare(b.monday_iso));
}

function loadRowsFromStorage(key: string): WeeklyLearningGuideNote[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return normalizeRows(parsed as WeeklyLearningGuideNote[]);
  } catch {
    return null;
  }
}

function saveRowsToStorage(key: string, rows: WeeklyLearningGuideNote[], workspaceId: string, force = false): void {
  if (typeof window === 'undefined') return;
  if (!force && !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    deviceCacheStorage.setItem(key, JSON.stringify(normalizeRows(rows)));
  } catch {
    // ignore quota errors
  }
}

function isMissingTableError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes("Could not find the table 'public.weekly_learning_guide_notes'");
}

function groupRowsKey(row: {
  workspace_id: string;
  academic_year: number;
  term_number: 1 | 2;
  grade_id: string;
}): string {
  return `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
}

function cacheGroupedRows(rows: WeeklyLearningGuideNote[]): void {
  const grouped = new Map<string, WeeklyLearningGuideNote[]>();
  rows.forEach((row) => {
    const key = groupRowsKey(row);
    const list = grouped.get(key) ?? [];
    list.push(row);
    grouped.set(key, list);
  });
  grouped.forEach((group) => {
    const sample = group[0];
    if (!sample) return;
    saveRowsToStorage(
      cacheKey(sample.workspace_id, sample.academic_year, sample.term_number, sample.grade_id),
      group,
      sample.workspace_id
    );
    saveWeeklyLearningGuideNotesCache(
      sample.workspace_id,
      sample.academic_year,
      sample.term_number,
      sample.grade_id,
      group
    );
  });
}

function mergeLocalRows(
  existing: WeeklyLearningGuideNote[],
  updates: WeeklyLearningGuideNoteUpsert[]
): WeeklyLearningGuideNote[] {
  const byMonday = new Map(existing.map((row) => [row.monday_iso, row] as const));
  const now = new Date().toISOString();
  updates.forEach((row) => {
    const prev = byMonday.get(row.monday_iso);
    byMonday.set(row.monday_iso, {
      id: prev?.id ?? `local-${row.term_number}-${row.monday_iso}`,
      workspace_id: row.workspace_id,
      grade_id: row.grade_id,
      academic_year: row.academic_year,
      term_number: row.term_number,
      monday_iso: row.monday_iso,
      note_text: row.note_text,
      created_at: prev?.created_at ?? now,
      updated_at: now,
    });
  });
  return normalizeRows(Array.from(byMonday.values()));
}

export async function getWeeklyLearningGuideNotes(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<WeeklyLearningGuideNote[]> {
  if (!workspaceId || !gradeId) return [];
  const cached = loadWeeklyLearningGuideNotesCache(workspaceId, academicYear, termNumber, gradeId);
  if (cached.found) return cached.rows;
  try {
    const { data, error } = await supabase
      .from('weekly_learning_guide_notes')
      .select(WEEKLY_LEARNING_GUIDE_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .order('monday_iso', { ascending: true });
    if (error) throw new Error(error.message);
    const rows = normalizeRows((data ?? []) as unknown as WeeklyLearningGuideNote[]);
    saveWeeklyLearningGuideNotesCache(workspaceId, academicYear, termNumber, gradeId, rows);
    return rows;
  } catch (error) {
    if (!isMissingTableError(error)) throw error;
    const localRows = loadRowsFromStorage(localFallbackKey(workspaceId, academicYear, termNumber, gradeId)) ?? [];
    saveWeeklyLearningGuideNotesCache(workspaceId, academicYear, termNumber, gradeId, localRows, true);
    return localRows;
  }
}

export async function upsertWeeklyLearningGuideNotes(
  rows: WeeklyLearningGuideNoteUpsert[]
): Promise<WeeklyLearningGuideNote[]> {
  const validRows = rows.filter(
    (row) =>
      !!row.workspace_id &&
      !!row.grade_id &&
      !!row.monday_iso &&
      Number.isInteger(row.academic_year) &&
      (row.term_number === 1 || row.term_number === 2)
  );
  if (validRows.length === 0) return [];
  const payload = validRows.map((row) => {
    const { id: _ignoredId, ...rest } = row;
    return {
      ...rest,
      note_text: row.note_text ?? '',
      updated_at: new Date().toISOString(),
    };
  });
  let savedRows: WeeklyLearningGuideNote[];
  try {
    const { data, error } = await supabase
      .from('weekly_learning_guide_notes')
      .upsert(payload, {
        onConflict: 'workspace_id,grade_id,academic_year,term_number,monday_iso',
      })
      .select(WEEKLY_LEARNING_GUIDE_SELECT);
    if (error) throw new Error(error.message);
    savedRows = normalizeRows((data ?? []) as unknown as WeeklyLearningGuideNote[]);
  } catch (error) {
    if (!isMissingTableError(error)) throw error;
    const updatedGroups = new Map<string, WeeklyLearningGuideNote[]>();
    validRows.forEach((row) => {
      const key = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
      if (updatedGroups.has(key)) return;
      const existing =
        loadRowsFromStorage(localFallbackKey(row.workspace_id, row.academic_year, row.term_number, row.grade_id)) ?? [];
      const sameGroupUpdates = validRows.filter(
        (candidate) =>
          candidate.workspace_id === row.workspace_id &&
          candidate.academic_year === row.academic_year &&
          candidate.term_number === row.term_number &&
          candidate.grade_id === row.grade_id
      );
      const merged = mergeLocalRows(existing, sameGroupUpdates);
      saveRowsToStorage(
        localFallbackKey(row.workspace_id, row.academic_year, row.term_number, row.grade_id),
        merged,
        row.workspace_id,
        true
      );
      saveRowsToStorage(cacheKey(row.workspace_id, row.academic_year, row.term_number, row.grade_id), merged, row.workspace_id);
      saveWeeklyLearningGuideNotesCache(
        row.workspace_id,
        row.academic_year,
        row.term_number,
        row.grade_id,
        merged,
        true
      );
      updatedGroups.set(key, merged);
    });
    const mergedRows: WeeklyLearningGuideNote[] = [];
    updatedGroups.forEach((group) => {
      mergedRows.push(...group);
    });
    return normalizeRows(mergedRows);
  }
  cacheGroupedRows(savedRows);
  const workspaceIds = Array.from(new Set(validRows.map((row) => row.workspace_id)));
  for (const workspaceId of workspaceIds) {
    await notifySchoolProgramMutation(workspaceId, ['weeklyGuide']);
  }
  return savedRows;
}
